import axios from "axios";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL } from "const";

export const callGetComputeEnvs = async (msalInstance: any): Promise<{
  clusters?: any[];
  workbenches?: any;
  version?: any;
} | null> => {
  const token = await getAccessToken(msalInstance);
  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

  const requestURL: string = NEXUS_API_BASE_URL + "/posit/get-compute-envs";

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: bearerToken,
    },
  };

  try {
    const response = await axios.get(requestURL, config);
    return (response.data?.data?.result as { clusters?: any[]; workbenches?: any; version?: any }) || null;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const msg = error.response?.data?.message || error.message || `Failed to fetch compute envs${status ? `: ${status}` : ""}`;
      throw new Error(msg);
    } else {
      throw new Error("Failed to fetch compute envs");
    }
  }
};

export const launchPositWorkbench = async (
  msalInstance: any,
  selectedProfile: string,
  region: string,
  bucketName: string,
  dataAccountId: string,
  spaceDisplayName: string,
  accessType: string,
  reason: string,
  resourceProfiles: any[],
  computeEnvs: any,
  selectedIDE?: string,
): Promise<string> => {
  const token = await getAccessToken(msalInstance);
  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

  const selectedResourceProfile = resourceProfiles.find((profile: any) => profile.name === selectedProfile);

  if (!selectedResourceProfile) {
    throw new Error("Selected resource profile not found");
  }

  const image =
    computeEnvs?.clusters?.[0]?.defaultImage || computeEnvs?.clusters?.[0]?.images?.[0]?.image || "";

  if (!image) {
    throw new Error("Workbench image not found in compute environments");
  }

  const payload = {
    region: region,
    bucket_name: bucketName,
    data_account_id: dataAccountId,
    space_name: spaceDisplayName,
    access_type: accessType,
    reason: reason,
    resource_profile: selectedResourceProfile,
    image: image,
    ...(selectedIDE ? { workbench: selectedIDE } : {}),
  };

  const requestURL = `${NEXUS_API_BASE_URL}/posit/launch-posit-session`;

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: bearerToken,
    },
  };

  try {
    const response = await axios.post(requestURL, payload, config);
    const data = response.data;

    if (data && data.data && data.data.url) {
      return data.data.url as string;
    } else {
      const maybeMsg = data?.message || "No URL received from the API";
      throw new Error(maybeMsg);
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      console.error("Launch Posit Workbench - Axios Error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      const status = error.response?.status;
      if (status === 404) {
        const detailMsg = error.response?.data?.detail || "User not found in posit workbench";
        throw new Error(detailMsg);
      }

      const serverMessage = error.response?.data?.detail || error.response?.data?.message || error.message || `Failed to launch Posit Workbench${error.response?.status ? `: ${error.response.status}` : ""}`;
      throw new Error(serverMessage);
    } else {
      console.error("Launch Posit Workbench - Unknown Error:", error);
      throw new Error("Failed to launch Posit Workbench");
    }
  }
};

export const callGetSession = async (msalInstance: any, spaceName: string): Promise<{
  hasActiveSession: boolean;
  url?: string;
  sessionData?: any;
} | null> => {
  const token = await getAccessToken(msalInstance);
  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

  const requestURL = `${NEXUS_API_BASE_URL}/posit/get-session?space_name=${spaceName}`;

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: bearerToken,
    },
  };

  try {
    const response = await axios.get(requestURL, config);
    const data = response.data;

    if (data?.data?.url) {
      return {
        hasActiveSession: true,
        url: data.data.url,
        sessionData: data.data,
      };
    }

    return {
      hasActiveSession: false,
    };
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 401) {
        throw new Error("Unable to connect to Posit Workbench.");
      }
      const serverMsg = error.response?.data?.detail || error.response?.data?.message;
      const msg = serverMsg || `Failed to check session${status ? `: ${status}` : ""}`;
      throw new Error(msg);
    } else {
      throw new Error("Failed to check Posit Workbench session");
    }
  }
};

export async function getSessionOrComputeEnvs(msalInstance: any, spaceName: string): Promise<{
  hasActiveSession: boolean;
  url?: string;
  sessionData?: any;
  computeEnvs?: any;
}> {
  try {
    const sessionResult = await callGetSession(msalInstance, spaceName);

    if (sessionResult?.hasActiveSession && sessionResult.url) {
      return {
        hasActiveSession: true,
        url: sessionResult.url,
        sessionData: sessionResult.sessionData,
        computeEnvs: null,
      };
    }

    const computeEnvs = await callGetComputeEnvs(msalInstance);
    return {
      hasActiveSession: false,
      url: undefined,
      sessionData: undefined,
      computeEnvs: computeEnvs,
    };
  } catch (error: any) {
    console.error("Error fetching session or compute envs:", error);
    throw error;
  }
}

export async function launchPositWorkbenchFlow(
  msalInstance: any,
  selectedProfile: string,
  accessScope: string,
  region: string,
  bucketName: string,
  dataAccountId: string,
  spaceDisplayName: string,
  reason: string,
  resourceProfiles: any[],
  computeEnvs: any,
  selectedIDE?: string,
) {
  return launchPositWorkbench(
    msalInstance,
    selectedProfile,
    region,
    bucketName,
    dataAccountId,
    spaceDisplayName,
    accessScope,
    reason,
    resourceProfiles,
    computeEnvs,
    selectedIDE,
  );
}
